import { Alignment, DOMUtil, ElementShaker, Logger, LogManager, ObjectUtil, ValueResolver } from "@mcleod/core";
import { AnchoredComponentPositionerProps } from "../positioner/AnchoredComponentPositioner";
import { Component } from "../..";
import { Panel } from "../../components/panel/Panel";
import { ScreenStack } from "../../components/panel/ScreenStack";
import { TooltipDisplayer } from "./TooltipDisplayer";
import { TooltipFiller } from "./TooltipFiller";
import { TooltipOptions } from "./TooltipOptions";
import { TooltipSource } from "./TooltipSource";
import { TooltipSourceType } from "./TooltipSourceType";

export class Tooltip extends Panel {
    private static _log: Logger;
    private _anchorComponent: Component;
    private options: TooltipOptions;
    private _sources: Map<TooltipSourceType, TooltipSource>;
    private _wasMounted = false;

    constructor(anchor: Component, sources: TooltipSource[], options?: TooltipOptions) {
        super({
            id: "tooltip_" + new Date().getTime(),
            scrollY: false,
            themeKey: "tooltip"
        });
        this.setInitialStyling();
        this.anchorComponent = anchor;
        this.sources = sources;
        this.setOptions(options);
        this.zIndex = ScreenStack.getNewHighestZIndex() ?? 5000;
        this.addMouseListeners();
    }

    get anchorComponent() {
        return this._anchorComponent;
    }

    private set anchorComponent(value: Component) {
        this._anchorComponent = value;
    }

    get allowNestedTooltips(): boolean {
        return this.options.allowNestedTooltips ?? true;
    }

    /**
     * Sets the tooltip's style to prevent it from being seen until it has been constructed & placed on the screen
     */
    private setInitialStyling() {
        this.setStyleProps({
            overflow: "visible",
            position: "absolute",
            visibility: "hidden"
        });
    }

    get sources(): Map<TooltipSourceType, TooltipSource> {
        return this._sources;
    }

    private set sources(sources: TooltipSource[]) {
        this._sources = new Map<TooltipSourceType, TooltipSource>();
        (sources ?? [])
            .filter(source => source != null && !source.isEmpty())
            .sort((a, b) => a.type - b.type)
            .forEach(source => this._sources.set(source.type, source));
    }

    get includesCurrencyExchangeInfo(): boolean {
        return this.sources.has(TooltipSourceType.CURRENCY_EXCHANGE_INFO);
    }

    private setOptions(constructorOptions: TooltipOptions) {
        this.options = {};
        const resolvedDynamicOptions =
            ValueResolver.resolve(this._anchorComponent.tooltipDynamicOptions, this.anchorComponent);
        if (resolvedDynamicOptions != null) {
            ObjectUtil.appendObject(resolvedDynamicOptions, this.options);
        }
        ObjectUtil.appendObject(constructorOptions, this.options);
    }

    private addMouseListeners() {
        if (this.anchorComponent != null) {
            this.addMouseEnterListener(() => this.anchorComponent.clearTooltipHideTimeout());
        }
        this.addMouseLeaveListener(event => {
            if (this.anchorComponent == null || !DOMUtil.isOrContains(this.anchorComponent._element, event.domEvent.toElement)) {
                const mouseOverTooltipIndex = this.getMouseOverTooltipIndex(event.domEvent.relatedTarget);
                // If the mouse isn't over any tooltip in the stack, hide them all
                if (mouseOverTooltipIndex < 0) {
                    TooltipDisplayer.removeAll();
                }
                else {
                    // If the mouse is over a tooltip, hide everything newer than that tooltip
                    const tooltipStackCopy = [...ScreenStack.getAllTooltips()];
                    for (let x = tooltipStackCopy.length - 1; x > mouseOverTooltipIndex; x--) {
                        TooltipDisplayer.remove(tooltipStackCopy[x]);
                    }
                }
            }
        });
    }

    private getMouseOverTooltipIndex(mouseOverElement: HTMLElement): number {
        const tooltipStackCopy = [...ScreenStack.getAllTooltips()];
        for (let x = 0; x <= tooltipStackCopy.length - 1; x++) {
            if (DOMUtil.isOrContains(tooltipStackCopy[x]._element, mouseOverElement)) {
                return x;
            }
        }
        return -1;
    }

    async fillAndShow() {
        await new TooltipFiller(this).fillAndShow();
    }

    public show() {
        TooltipDisplayer.show(this);
    }

    public makeVisible() {
        this.style.visibility = "visible";
    }

    public remove() {
        TooltipDisplayer.remove(this);
    }

    public doBeforeRemove() {
        this._wasMounted =  this.isMounted;
    }

    get removed() {
        return this._wasMounted && !this.isMounted;
    }

    get removedOrAnchorRemoved(): boolean {
        return this.removed ||
               (this.anchorComponent != null && !this.anchorComponent.isMounted);
    }

    public startTimeout() {
        if (this.options.timeout != null) {
            setTimeout(() => TooltipDisplayer.removeAll(), this.options.timeout);
        }
    }

    public shake() {
        if (this.options.shaking === true) {
            ElementShaker.shake(this._element, 8);
        }
    }

    get positionerProps(): AnchoredComponentPositionerProps {
        return {
            anchorProps: {
                anchor: this.anchorComponent,
                position: this.options.position,
                align: Alignment.CENTER
            },
            anchoredComponent: this,
            showPointer: this.options.showPointer !== false
        }
    }

    public static get log() {
        if (Tooltip._log == null)
            Tooltip._log = LogManager.getLogger("components.page.tooltips.Tooltip");
        return Tooltip._log;
    }
}
